import React from "react";
import { Formik } from "formik";

import { FormLabel } from "@mui/material";

// Styling
import s from "./LoginForm.module.scss";
// Components
import FormikTextField from "~/components/_uikit/FormikTextField";
import FormikCheckbox from "~/components/_uikit/FormikCheckbox";
import FormikRadioGroup from "~/components/_uikit/FormikRadioGroup";
import TextFieldWithMask from "~/components/_uikit/TextFieldWithMask";
// Types
import { Sex, INewPatientForm } from "~/types/patient";
// FormValidation
import Yup from "~/utils/plugins/Yup";
import { INewPatientEntity } from "~/types/patient";
import { logEvent } from "~/utils/amplitude";
import { values } from "mobx";
import debounce from "lodash.debounce";

interface Props {
  loginCallback: (payload: INewPatientForm, callback: () => void) => void;
  regionId: number | null;
  savedPatient: INewPatientEntity | null;
}

const INITIAL_LOGIN_FORM_STATE: INewPatientForm = {
  lname: "",
  fname: "",
  mname: "",
  birthday: "",
  insurance_policy: "",
  id_gender: Sex.male,
  personal: false,
};

const FORM_VALIDATION = Yup.object().shape({
  fname: Yup.string()
    .trim()
    .required()
    .matches(/^[a-zA-zЁёА-я-]*$/, "Фамилия может содержать только буквы"),
  lname: Yup.string()
    .trim()
    .required()
    .matches(/^[a-zA-zЁёА-я-]*$/, "Имя может содержать только буквы"),
  mname: Yup.string().matches(
    /^[a-zA-zЁёА-я-]*$/,
    "Отчество может содержать только буквы"
  ),
  birthday: Yup.string()
    .required()
    .matches(
      /^\s*(3[01]|[12][0-9]|0?[1-9])\.(1[012]|0?[1-9])\.((?:19|20)\d{2})\s*$/,
      "Неверный формат даты"
    ),
  insurance_policy: Yup.string()
    .matches(/^(.*)?\S+(.*)?$/, "Не пустое поле")
    .required()
    .matches(/^[0-9]+$/, "Только числа разрешены")
    .min(10, "Укажите верный формат полиса")
    .max(16, "Укажите верный формат полиса"),
  id_gender: Yup.number().required().oneOf([Sex.female, Sex.male]),
  personal: Yup.boolean().required().oneOf([true], "Обязательное поле"),
});

const LoginForm: React.FC<Props> = ({
  loginCallback,
  regionId,
  savedPatient,
}) => {
  const debouncedLogEvent = debounce(logEvent, 2500);

  return (
    <Formik
      initialValues={{
        ...INITIAL_LOGIN_FORM_STATE,
        ...(savedPatient ?? {}),
      }}
      validationSchema={FORM_VALIDATION}
      validateOnMount
      onSubmit={(values, actions) => {
        actions.setSubmitting(true);
        actions.setFieldValue("personal", false);
        loginCallback(values, () => {
          actions.setSubmitting(false);
        });
      }}
    >
      {({ isValid, handleSubmit, isSubmitting }) => {
        return (
          <form className={s.form} onSubmit={handleSubmit}>
            <div className={s.formContainer}>
              <FormikTextField
                autoFocus
                name="lname"
                label="Фамилия"
                fullWidth
                onChange={(event) =>
                  debouncedLogEvent(
                    "Д/ Ввел фамилию пациента / Авторизация пациента",
                    {
                      "Введенные данные": event.target.value,
                    }
                  )
                }
              />
              <FormikTextField
                name="fname"
                label="Имя"
                fullWidth
                onChange={(event) =>
                  debouncedLogEvent(
                    "Д/ Ввел имя пациента / Авторизация пациента",
                    {
                      "Введенные данные": event.target.value,
                    }
                  )
                }
              />
              <FormikTextField
                name="mname"
                label="Отчество"
                fullWidth
                onChange={(event) =>
                  debouncedLogEvent(
                    "Д/ Ввел отчество пациента / Авторизация пациента",
                    {
                      "Введенные данные": event.target.value,
                    }
                  )
                }
              />
              <TextFieldWithMask
                name="birthday"
                placeholder="__.__.____"
                label="Дата рождения"
                fullWidth
                onChange={(event) =>
                  debouncedLogEvent(
                    "Д/ Ввел дату рождения / Авторизация пациента",
                    {
                      "Введенные данные": event.target.value,
                    }
                  )
                }
              />
              <TextFieldWithMask
                name="insurance_policy"
                mask="#### #### #### ####"
                label="Полис"
                fullWidth
                onChange={(event) =>
                  debouncedLogEvent("Д/ Ввел полис / Авторизация пациента", {
                    "Введенные данные": event.target.value,
                  })
                }
              />
            </div>
            <div className={s.formGender}>
              <FormikRadioGroup
                name="id_gender"
                options={[
                  { value: Sex.male, label: "мужской" },
                  { value: Sex.female, label: "женский" },
                ]}
                onChange={(event) =>
                  logEvent("Д/ Выбрал пол пациента / Авторизация пациента", {
                    "Введенные данные":
                      event.target.value === Sex.male ? "мужской" : "женский",
                  })
                }
              />
            </div>
            <div>
              <FormLabel
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
                onChange={(event) =>
                  logEvent(
                    "Д/ Отметил согласие на обработку перс данных / Авторизация пациента"
                  )
                }
              >
                <FormikCheckbox
                  name="personal"
                  sx={{
                    display: "inline-block",
                    marginRight: "10px",
                  }}
                />
                <span className={s.formPolicyCheckbox}>
                  Я даю согласие на{" "}
                  <a
                    href="https://vrach.f.x.eltc.ru/doctor/zapis_k_vrachy.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={s.formPolicyLink}
                  >
                    обработку своих персональных данных
                  </a>
                </span>
              </FormLabel>
            </div>
            {!regionId && (
              <p className={s.formWarning}>
                Для продолжения работы выберите регион или разрешите геолокацию
                на устройстве и обновите страницу
              </p>
            )}
            <div className={s.formConfirm}>
              <button
                className={`button ${
                  !isValid || !regionId || isSubmitting
                    ? "button--disabled"
                    : ""
                } ${s.formConfirmBtn}`}
                type="submit"
                disabled={!isValid || !regionId || isSubmitting}
              >
                Войти
              </button>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};

export default LoginForm;
